"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { RestrictedIcd10Resource } from "@/components/icd10/restricted-icd10-resource";
import { Paywall } from "@/components/common/paywall";
import { AccessInfo } from "@/components/common/access-info";
import { ResourceContext } from "@/contexts/resource-context";
import { cn } from "@/lib/utils";
import { ChevronDownIcon } from "lucide-react";
import { Icd10Inn } from "@/components/icd10/icd10-resource/cards/icd10-inn";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/motion-primitives/accordion";
import { Icd10Med } from "@/components/icd10/icd10-resource/cards/icd10-med";
import { Button } from "@/components/ui/button";
import { SuperLink as Link } from "@/components/common/super-link";
import { createPath } from "@/service/routing/routing";
import { useSearchParams } from "next/navigation";
import { scrollToPos } from "@/service/scroll/scroll";

export const Icd10CodeExtended = ({ code: codeProp }: { code: ICD10VM }) => {
  const {
    slug,
    code,
    groups,
    inns,
    description,
    description2,
    name,
    id,
    hasMoreMedGroups,
  } = codeProp;
  const { access } = useContext(ResourceContext) as ResourceInfo;
  const { granted } = access as ResourceAccess;
  const searchParams = useSearchParams();
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLInputElement>(null);
  const hasContent =
    (groups.length || inns.length || description || description2) &&
    !(!granted && !description && !description2);

  const subslug = searchParams.get("subslug");

  useEffect(() => {
    if (subslug === slug) {
      setShow(true);
      scrollToPos({
        top: ref?.current?.getBoundingClientRect()?.top - 64,
      });
    }
  }, []);

  return (
    <div className={"my-4"} ref={ref} id={code} data-slug={slug}>
      <div
        className={cn(
          "text-foreground group flex items-center justify-between gap-4 rounded-lg transition-all",
          hasContent && "hover:cursor-pointer",
        )}
        onClick={() => {
          setShow(!show);
        }}
      >
        <div className={"flex items-center gap-4"}>
          <div
            className={cn(
              `flex h-7 w-12 min-w-12 items-center justify-center rounded-lg text-xs font-bold text-white`,
              "bg-zinc-500",
            )}
          >
            {code}
          </div>

          <div
            className={`text-foreground group-hover:text-remedium-blue text-sm font-normal transition-all`}
          >
            {name}
          </div>
        </div>

        {hasContent && (
          <ChevronDownIcon
            className={cn("transition-all", show && "rotate-180")}
            size={"16px"}
          />
        )}
      </div>

      {hasContent && (
        <div className={cn("hidden", show && "ml-16 block")} ref={contentRef}>
          {description && (
            <p className={`text-foreground my-4 text-sm font-semibold`}>
              {description}
            </p>
          )}
          {description2 && (
            <p className={"text-foreground my-4 text-sm font-semibold"}>
              {description2}
            </p>
          )}

          <Paywall>
            <Accordion className={"w-full"}>
              {inns.length > 0 && (
                <AccordionItem value={"inns"} className={"my-4"}>
                  <AccordionTrigger
                    className={`flex w-full items-center justify-between rounded-lg bg-zinc-50 px-4 py-2.5 text-sm font-normal text-zinc-900 select-none hover:cursor-pointer`}
                  >
                    <span>
                      Lista substancji czynnych dla <strong>{code}</strong>
                    </span>
                    <ChevronDownIcon size={"16px"} />
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    <div className={"flex flex-wrap gap-2 py-4"}>
                      {inns.map((inn) => (
                        <Icd10Inn code={code} inn={inn} key={inn.name} />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {groups.length > 0 && (
                <AccordionItem value={"meds"} className={"my-4"}>
                  <AccordionTrigger
                    className={`flex w-full items-center justify-between rounded-lg bg-zinc-50 px-4 py-2.5 text-sm font-normal text-zinc-900 hover:cursor-pointer`}
                  >
                    <span>
                      Lista leków dla <strong>{code}</strong>
                    </span>
                    <ChevronDownIcon size={"16px"} />
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    <div className={"flex flex-col flex-wrap gap-2 py-4"}>
                      {groups.map((med) => (
                        <Icd10Med med={med} code={code} key={med.name} />
                      ))}
                    </div>
                    {hasMoreMedGroups && (
                      <Button size={"sm"} asChild={true}>
                        <Link href={createPath("meds.icd10", { id })}>
                          Zobacz pełną listę
                        </Link>
                      </Button>
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </Paywall>

          <AccessInfo>
            <RestrictedIcd10Resource type={"meds"} />
          </AccessInfo>
        </div>
      )}
    </div>
  );
};
